// НОМЕРНЫЕ ТАБЫ ЧАРТА ШАГА 5 — правило размещения v7 (B7-3). Порт `chartHTML()` борда
// `Life Map - s5 Red Line v7.html` `:512–552` (решения `s680.d1` п.2, `s681.d1`).
//
// ⚠️ ЭТО НЕ ПРАВИЛО §IV ПАСПОРТА, хотя происхождение общее (F4). У шага 5 свои числа: плашка 22×14,
// ярус 18, зазор 3, смещения 18 / 32 / 46, у краёв плашка прижимается к рамке. Спека шага 5,
// `s7.chart.numbered_points`: «у паспортной §IV правило своё, со своими числами 26×16 — адрес вне
// спеки шага 5». Общее с §IV только то, что общее по построению: примитивы пересечений и
// сэмплер кривой (`ivTabPlacement.js`) — одна реализация на оба экрана.
//
// ⭐ Кривая сэмплируется чистым JS (`samplePath`), а не `getPointAtLength` борда: jsdom его не
// умеет, и экран упал бы в тестах — причина та же, что у §IV (cc-76).

import { INFL, RHIT, SEGRECT, SEGSEG, samplePath } from '../../render/sections/ivTabPlacement.js';

export const TAB_W = 22;
export const TAB_H = 14;
const MARG = 3;
const TIER = 18;
const SAMPLE_STEP = 5; // `pathPts(solidD, 5)` `:513`
const DOT_HALF = 8; // `dotBox` `:514`
const MAGS = [18, 32, 46];

/**
 * Разместить табы по носителям.
 *
 * @param {number[]} ev индексы этапов-носителей по порядку (полный список, без кэпа карточек)
 * @param {(i: number) => number} xMid центр колонки
 * @param {(i: number) => number} yAt высота точки носителя (по заполненной несущей)
 * @param {string} red `d` красной линии
 * @param {{top: number, h: number, W: number}} frame верх полосы значений, высота чарта, ширина
 * @returns {Array<{i: number, n: string, cx: number, dy: number, x: number, y: number, up: boolean,
 *   tier: number, side: 'left'|'right', soft?: number, fallback?: number}>}
 */
export function placeTabsS5(ev, xMid, yAt, red, frame) {
  const { top, h, W } = frame;
  const curve = [];
  {
    const smp = samplePath(red, SAMPLE_STEP);
    for (let k = 1; k < smp.length; k++) curve.push({ x1: smp[k - 1].x, y1: smp[k - 1].y, x2: smp[k].x, y2: smp[k].y });
  }
  const dotBox = ev.map((i) => ({ x0: xMid(i) - DOT_HALF, x1: xMid(i) + DOT_HALF, y0: yAt(i) - DOT_HALF, y1: yAt(i) + DOT_HALF }));
  const romanBand = { x0: -1e4, x1: 1e4, y0: top - 18, y1: top - 5 };
  const placed = [romanBand];
  const leaders = [];
  const hard = (r, sg) => {
    let k = 0;
    placed.forEach((p) => { if (RHIT(INFL(r, MARG), p)) k++; });
    leaders.forEach((l) => { if (SEGRECT(l, INFL(r, 2))) k++; });
    placed.forEach((p) => { if (SEGRECT(sg, INFL(p, 2))) k++; });
    leaders.forEach((l) => { if (SEGSEG(sg, l)) k++; });
    dotBox.forEach((b) => { if (RHIT(r, b)) k++; });
    if (curve.some((cv) => SEGRECT(cv, INFL(r, 4)))) k++;
    return k;
  };
  const soft = (sg) => curve.reduce((k, cv) => k + (SEGSEG(sg, cv) ? 1 : 0), 0);

  return ev.map((i, j) => {
    const x = xMid(i), y = yAt(i), up = y > top + 46, pref = j % 2 ? 1 : -1;
    const cand = (side, tier, mag, uv) => {
      const dx = side * mag, base = uv ? -26 : 28, dy = base + (uv ? -tier * TIER : tier * TIER);
      // у краёв плашка прижимается к рамке, а не выносится за неё (`:527–530`)
      const cx = Math.min(Math.max(x + dx, 1 + TAB_W / 2), W - 1 - TAB_W / 2);
      return {
        cx, dy, tier, side, up: uv,
        r: { x0: cx - TAB_W / 2, x1: cx + TAB_W / 2, y0: y + dy - TAB_H / 2, y1: y + dy + TAB_H / 2 },
        s: { x1: cx, y1: y + dy + (uv ? 7 : -7), x2: x, y2: y + (uv ? -6 : 6) },
      };
    };
    const inFrame = (cd) => cd.r.y0 >= 1 && cd.r.y1 <= h - 1;
    const list = [];
    [up, !up].forEach((uv) => [pref, -pref].forEach((side) => MAGS.forEach((mag) => {
      for (let t = 0; t < 10; t++) list.push(cand(side, t, mag, uv));
    })));
    const ok = list.filter(inFrame);
    const pool = (ok.length ? ok : list).map((cd) => Object.assign(cd, { h: hard(cd.r, cd.s) }));
    const clean = pool.filter((cd) => cd.h === 0);
    let pick;
    if (clean.length) {
      clean.forEach((cd) => { cd.sf = soft(cd.s); });
      pick = clean.slice().sort((a, b) => a.sf - b.sf || a.tier - b.tier || Math.abs(a.cx - x) - Math.abs(b.cx - x))[0];
      if (pick.sf) pick.soft = pick.sf;
    } else {
      pick = pool.slice().sort((a, b) => a.h - b.h)[0];
      pick.fallback = pick.h;
    }
    placed.push(pick.r);
    leaders.push(pick.s);
    return {
      i, n: String(j + 1).padStart(2, '0'), cx: pick.cx, dy: pick.dy, x, y, up: pick.up,
      tier: pick.tier, side: pick.side < 0 ? 'left' : 'right',
      ...(pick.soft ? { soft: pick.soft } : {}),
      ...(pick.fallback ? { fallback: pick.fallback } : {}),
    };
  });
}
